import { TFunction } from '@lyearn/i18n';
import {
  CheckmarkCircleOutline,
  CloseCircleOutline,
  TargetOutline,
  TimerOutline,
} from '@lyearn/icons';
import { IconContainerProps } from '@/modules/corporate/modules/quiz/views/QuizPlayer/components/IconContainer/types';
import { roundedTillTwoDecimal } from '@/modules/user-survey/views/UserSurveyAnalytics/views/SurveyDetails/utils/roundedTillTwoDecimal';

import PartiallyCorrectIcon from '../PartiallyCorrectIcon';

export const SCORE_ICON_PROPS: IconContainerProps = {
  Icon: TargetOutline,
  classes: {
    container: 'bg-bg-cyan-light',
    icon: 'text-icon-cyan',
  },
  fillColorToken: 'icon-cyan',
};

export const CORRECT_ICON_PROPS: IconContainerProps = {
  Icon: CheckmarkCircleOutline,
  classes: {
    container: 'bg-border-success',
    icon: 'text-icon-success',
  },
  fillColorToken: 'icon-success',
};

export const PARTIALLY_CORRECT_ICON_PROPS: IconContainerProps = {
  Icon: PartiallyCorrectIcon,
  classes: {
    container: 'bg-border-warning',
    icon: 'text-icon-warning',
  },
  fillColorToken: 'icon-warning',
};

export const INCORRECT_ICON_PROPS: IconContainerProps = {
  Icon: CloseCircleOutline,
  classes: {
    container: 'bg-border-error',
    icon: 'text-icon-error',
  },
  fillColorToken: 'icon-error',
};

export const TIMESPENT_ICON_PROPS: IconContainerProps = {
  Icon: TimerOutline,
  classes: {
    container: 'bg-bg-tertiary',
    icon: 'text-icon-secondary',
  },
  fillColorToken: 'icon-secondary',
};

export function formatTimeSpent(timeSpent: number) {
  const hours = Math.floor(timeSpent / 3600);
  const minutes = Math.floor(timeSpent / 60);
  const seconds = Math.floor(timeSpent % 60);
  if (hours > 0) {
    return `${hours.toString()}h` + (minutes > 0 ? ` ${minutes.toString()}m` : '');
  }

  return minutes > 0 ? `${minutes.toString()}m ${seconds.toString()}s` : `${seconds.toString()}s`;
}

export const getReviewGridItems = ({
  t,
  correctCount,
  partiallyCorrectCount,
  incorrectCount,
  passingPercentage,
  score,
  maxScore,
  timeSpent,
}: {
  t: TFunction;
  correctCount: number;
  partiallyCorrectCount: number;
  incorrectCount: number;
  passingPercentage: number;
  score: number;
  maxScore: number;
  timeSpent: number;
}) => {
  return [
    {
      iconContainerProps: CORRECT_ICON_PROPS,
      label: t('QuizPlayer:Correct'),
      value: t('QuizPlayer:Response', { count: correctCount }),
    },
    ...(partiallyCorrectCount
      ? [
          {
            iconContainerProps: PARTIALLY_CORRECT_ICON_PROPS,
            label: t('QuizPlayer:PartiallyCorrectLabel'),
            value: t('QuizPlayer:Response', { count: partiallyCorrectCount }),
          },
        ]
      : []),
    {
      iconContainerProps: INCORRECT_ICON_PROPS,
      label: t('QuizPlayer:Incorrect'),
      value: t('QuizPlayer:Response', { count: incorrectCount }),
    },
    {
      iconContainerProps: SCORE_ICON_PROPS,
      label: t('QuizPlayer:Score'),
      value: `${roundedTillTwoDecimal(score)}/${roundedTillTwoDecimal(maxScore)}`,
    },
    ...(timeSpent
      ? [
          {
            iconContainerProps: TIMESPENT_ICON_PROPS,
            label: t('QuizPlayer:TimeSpent'),
            value: formatTimeSpent(timeSpent),
          },
        ]
      : []),
  ];
};
